import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import {
  Plus,
  ClipboardList,
  Clock,
  User,
  Calendar,
  CheckCircle,
  AlertCircle,
  PlayCircle,
  BarChart3,
  FileText,
  QrCode,
  Grid3X3,
  TrendingUp,
  Award,
  Bug,
  Eye,
  ExternalLink
} from 'lucide-react';

// Utility function for class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Updated ChillerAuditDashboard Component with Real Statistics
export default function ChillerAuditDashboard({
  onStartNewAudit,
  user,
  availableDrafts = [],
  onResumeDraft,
  onViewCompletedAudit,
  isCreatingNewAudit = false,
  className,
  statistics = null // New prop for statistics
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('completed');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDebug, setShowDebug] = useState(false);
  const itemsPerPage = 10;
  
  // Debug: Log all data
  useEffect(() => {
    console.log('=== ChillerAuditDashboard Debug ===');
    console.log('Total audits received:', availableDrafts.length);
    console.log('Statistics received:', statistics);
    console.log('All audits:', availableDrafts);
    
    // Group by status
    const statusGroups = availableDrafts.reduce((acc, audit) => {
      const status = audit.status || 'UNKNOWN';
      if (!acc[status]) acc[status] = [];
      acc[status].push(audit);
      return acc;
    }, {});
    
    console.log('Audits grouped by status:', statusGroups);
    
    // Log each status count
    Object.entries(statusGroups).forEach(([status, audits]) => {
      console.log(`${status}: ${audits.length} audits`);
    });
  }, [availableDrafts, statistics]);

  // Separate drafts and completed audits
  const draftAudits = availableDrafts.filter(d => d.status === 'DRAFT');
  const completedAudits = availableDrafts.filter(d => d.status === 'SUBMITTED');
  
  // DEBUG: Log completed audits to see what data we have
  console.log('🔥 COMPLETED AUDITS RECEIVED:', completedAudits.length, 'audits');
  if (completedAudits.length > 0) {
    console.log('🔥 FIRST COMPLETED AUDIT SAMPLE:', completedAudits[0]);
  }
  
  // Also check for any other statuses
  const otherStatusAudits = availableDrafts.filter(d => d.status !== 'DRAFT' && d.status !== 'SUBMITTED');

  // Pagination logic
  const getCurrentPageItems = (items) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items) => Math.ceil(items.length / itemsPerPage);

  // Reset page when switching tabs
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Format audit information for display
  const formatAuditForTable = (audit, index) => {
    const createdAt = audit.submitted_at ? new Date(audit.submitted_at) : new Date(audit.created_at);
    
    // DEBUG: Log the audit object to see what data is available
    console.log(`🔍 DEBUG: Audit ${index} data:`, {
      audit_id: audit.audit_form_response_id,
      serial_number: audit.serial_number,
      chiller_serial_number: audit.chiller_serial_number,
      raw_response_json: audit.raw_response_json,
      work_order: audit.work_order
    });
    
    // Extract chiller serial number - prioritize backend field, then raw_response_json
    let chillerSerialNumber = audit.serial_number || audit.chiller_serial_number || '';
    if (!chillerSerialNumber && audit.raw_response_json) {
      chillerSerialNumber = audit.raw_response_json.serialNumber || 
                           audit.raw_response_json.unitDetailsData?.serialNumber || 
                           '';
    }
    // Don't use session ID as fallback for serial number - keep it empty if not provided
    if (!chillerSerialNumber) {
      chillerSerialNumber = 'N/A';
    }
    
    console.log(`🔍 DEBUG: Extracted chillerSerialNumber: "${chillerSerialNumber}"`)
    
    // Extract work order (OV number) - this is the OV# column
    let workOrder = audit.work_order || '';
    if (!workOrder && audit.raw_response_json) {
      workOrder = audit.raw_response_json.workOrder || 
                  audit.raw_response_json.unitDetailsData?.workOrder || 
                  '';
    }
    if (!workOrder) {
      workOrder = 'N/A';
    }
    
    // Extract model number
    let modelNumber = audit.unit_model || '';
    if (!modelNumber) {
      modelNumber = audit.audit_form?.product?.model_number || '';
    }
    if (!modelNumber && audit.raw_response_json) {
      modelNumber = audit.raw_response_json.unitModel || 
                    audit.raw_response_json.unitDetailsData?.unitModel || 
                    '';
    }
    if (!modelNumber) {
      modelNumber = audit.audit_form?.form_code || 'N/A';
    }
    
    // Extract production line
    let productionLine = audit.production_line || '';
    if (!productionLine && audit.raw_response_json) {
      productionLine = audit.raw_response_json.productionLine || 
                      audit.raw_response_json.unitDetailsData?.productionLine || 
                      '';
    }
    if (!productionLine) {
      productionLine = audit.shift || 'N/A';
    }
    
    const score = audit.score_percentage || 0;
    
    return {
      sn: index + 1,
      chillerSerialNumber: chillerSerialNumber,
      score: Math.round(score),
      workOrder: workOrder,
      modelNumber: modelNumber,
      productionLine: productionLine,
      dateTime: `${createdAt.toLocaleDateString('en-GB').replace(/\//g, '-')}, ${createdAt.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })}`,
      audit: audit
    };
  };

  // Handle viewing completed audit
  const handleViewCompletedAudit = (audit) => {
    console.log('Viewing completed audit:', audit);
    if (onViewCompletedAudit) {
      onViewCompletedAudit(audit);
    }
  };

  // Get statistics values with fallbacks
  const getStatValue = (key, fallback) => {
    if (statistics && statistics[key] !== undefined && statistics[key] !== null) {
      return statistics[key];
    }
    return fallback;
  };

  return (
    <section className={cn("fixed inset-0 flex flex-col bg-gray-50 overflow-hidden", className)}>
      <div className="max-w-7xl mx-auto w-full flex flex-col h-full overflow-hidden">
        {/* Logo and Title Section */}
        <div className="flex-shrink-0 mb-3 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/daikin-logo.png"
              alt="Daikin Applied Europe"
              className="h-8 w-auto"
            />

          </div>
        </div>

        {/* Top Action Buttons */}
        <div className="flex-shrink-0 px-4 grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Start New Audit - Enter */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-2 border-gray-200 hover:border-blue-400 transition-all cursor-pointer h-40"
                  onClick={() => onStartNewAudit('enter')}>
              <CardContent className="flex flex-col items-center justify-center h-full p-6">
                <Grid3X3 className="h-12 w-12 text-gray-600 mb-3" />
                <h3 className="text-lg font-semibold text-gray-800">{t('dashboard.start_new_audit')}</h3>
                <p className="text-sm text-gray-500 mt-1">{t('dashboard.enter_serial')}</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Start New Audit - Scan */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card className="border-2 border-gray-200 hover:border-blue-400 transition-all cursor-pointer h-40"
                  onClick={() => onStartNewAudit('scan')}>
              <CardContent className="flex flex-col items-center justify-center h-full p-6">
                <QrCode className="h-12 w-12 text-gray-600 mb-3" />
                <h3 className="text-lg font-semibold text-gray-800">{t('dashboard.start_new_audit')}</h3>
                <p className="text-sm text-gray-500 mt-1">{t('dashboard.scan_serial')}</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Key Performance Indicators */}
        <div className="flex-shrink-0 px-4 grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card className="bg-white h-28">
              <CardContent className="p-4 h-full flex items-center justify-center">
                <div className="text-center space-y-1">
                  <p className="text-xs text-gray-500">{t('dashboard.wip_audit')}</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {getStatValue('draft_audits', draftAudits.length)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card className="bg-white h-28">
              <CardContent className="p-4 h-full flex items-center justify-center">
                <div className="text-center space-y-1">
                  <p className="text-xs text-gray-500">{t('dashboard.avg_time_per_audit')}</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {getStatValue('average_time_formatted', '3:03')}
                    <span className="text-sm font-normal">{t('dashboard.hrs')}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <Card className="bg-white h-28">
              <CardContent className="p-4 h-full flex items-center justify-center">
                <div className="text-center space-y-1">
                  <p className="text-xs text-gray-500">{t('dashboard.audits_up_to_date')}</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {getStatValue('completed_audits', completedAudits.length)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <Card className="bg-white h-28">
              <CardContent className="p-4 h-full flex items-center justify-center">
                <div className="text-center space-y-1">
                  <p className="text-xs text-gray-500">{t('dashboard.average_score')}</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {Math.round(getStatValue('average_score', completedAudits.length > 0
                      ? completedAudits.reduce((sum, a) => sum + (a.score_percentage || 0), 0) / completedAudits.length
                      : 95
                    ))}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Audit Reports with Tabs */}
        <motion.div
          className="flex-1 overflow-hidden px-4 pb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <Card className="bg-white h-full flex flex-col">
            <CardContent className="p-6 flex flex-col h-full overflow-hidden">
              {/* Tabs */}
              <div className="flex-shrink-0 flex items-center border-b mb-4">
                <button
                  onClick={() => setActiveTab('completed')}
                  className={cn(
                    "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                    activeTab === 'completed'
                      ? "border-green-500 text-green-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  )}
                >
                  <CheckCircle className="h-4 w-4 inline mr-1" />
                  {t('dashboard.completed_audits')} ({completedAudits.length})
                </button>
                <button
                  onClick={() => setActiveTab('drafts')}
                  className={cn(
                    "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ml-4",
                    activeTab === 'drafts'
                      ? "border-orange-500 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  )}
                >
                  <Clock className="h-4 w-4 inline mr-1" />
                  {t('dashboard.draft_audits')} ({draftAudits.length})
                </button>
              </div>

              {/* Tab Content - Same as before but using real data */}
              {activeTab === 'completed' ? (
                <div className="flex-1 overflow-hidden flex flex-col">
                  {completedAudits.length > 0 ? (
                    <>
                      <div className="flex-1 overflow-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">{t('dashboard.table_sn')}</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">{t('dashboard.table_chiller_sn')}</th>
                              <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider py-2">{t('dashboard.table_score')}</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">{t('dashboard.table_ov')}</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">{t('dashboard.table_model')}</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">{t('dashboard.table_pl')}</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">{t('dashboard.table_date_time')}</th>
                              <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider py-2">{t('dashboard.table_action')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getCurrentPageItems(completedAudits).map((audit, index) => {
                              const globalIndex = ((currentPage - 1) * itemsPerPage) + index;
                              const auditInfo = formatAuditForTable(audit, globalIndex);
                              console.log(auditInfo, ' is auditInfo')
                              return (
                                <tr 
                                  key={audit.audit_form_response_id} 
                                  className="border-b hover:bg-gray-50 cursor-pointer"
                                  onClick={() => handleViewCompletedAudit(audit)}
                                >
                                  <td className="py-2 text-sm">{auditInfo.sn}</td>
                                  <td className="py-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
                                    {auditInfo.chillerSerialNumber}
                                  </td>
                                  <td className="py-2 text-sm text-center">
                                    <span className={cn(
                                      "inline-flex items-center justify-center w-7 h-7 rounded text-xs",
                                      auditInfo.score >= 95 ? "bg-green-100 text-green-700" : 
                                      auditInfo.score >= 80 ? "bg-yellow-100 text-yellow-700" :
                                      "bg-red-100 text-red-700"
                                    )}>
                                      {auditInfo.score}
                                    </span>
                                  </td>
                                  <td className="py-2 text-xs font-mono">{auditInfo.workOrder}</td>
                                  <td className="py-2 text-xs">{auditInfo.modelNumber}</td>
                                  <td className="py-2 text-sm">{auditInfo.productionLine}</td>
                                  <td className="py-2 text-xs text-gray-600">{auditInfo.dateTime}</td>
                                  <td className="py-2 text-sm text-center">
                                    <button
                                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewCompletedAudit(audit);
                                      }}
                                      title={t('dashboard.view_audit_details')}
                                    >
                                      <Eye className="h-4 w-4 inline" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination - Fixed at bottom */}
                      {getTotalPages(completedAudits) > 1 && (
                        <div className="flex-shrink-0 flex items-center justify-between mt-4 pt-4 border-t">
                          <p className="text-sm text-gray-500">
                            {t('dashboard.showing_audits')} {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, completedAudits.length)} {t('dashboard.of_audits')} {completedAudits.length} {t('dashboard.audits')}
                          </p>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                              className="px-3 py-1 text-sm bg-gray-200 text-gray-600 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {t('dashboard.previous')}
                            </button>
                            {[...Array(getTotalPages(completedAudits))].map((_, i) => (
                              <button
                                key={i + 1}
                                onClick={() => setCurrentPage(i + 1)}
                                className={cn(
                                  "px-3 py-1 text-sm rounded",
                                  currentPage === i + 1
                                    ? "bg-green-600 text-white"
                                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                                )}
                              >
                                {i + 1}
                              </button>
                            ))}
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(getTotalPages(completedAudits), prev + 1))}
                              disabled={currentPage === getTotalPages(completedAudits)}
                              className="px-3 py-1 text-sm bg-gray-200 text-gray-600 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {t('dashboard.next')}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">{t('dashboard.no_completed_audits')}</p>
                      <p className="text-xs text-gray-400 mt-1">{t('dashboard.completed_audits_appear')}</p>
                      {showDebug && statistics && (
                        <div className="mt-4 p-4 bg-yellow-50 rounded text-left">
                          <p className="text-sm font-semibold text-yellow-800">Debug Info:</p>
                          <p className="text-xs text-yellow-700">
                            API Stats: {statistics.total_audits} total, 
                            {statistics.completed_audits} completed, 
                            {statistics.draft_audits} drafts
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            Frontend Filter: Found {completedAudits.length} completed audits
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1 overflow-hidden flex flex-col">
                  {draftAudits.length > 0 ? (
                    <>
                      <div className="flex-1 overflow-auto space-y-2">
                        {getCurrentPageItems(draftAudits).map((draft) => {
                          const auditInfo = formatAuditForTable(draft, 0);
                          console.log(draft)
                          return (
                            <div 
                              key={draft.audit_form_response_id}
                              className="bg-orange-50 border border-orange-200 rounded-lg p-3"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900 text-sm">
                                    {auditInfo.formName}
                                  </h4>
                                  <div className="text-xs text-gray-600 mt-0.5 flex items-center gap-3">
                                    <span>Started: {auditInfo.dateTime}</span>
                                    {draft.work_order && <span>Work Order: {draft.work_order}</span>}
                                    {draft.shift && <span>Shift: {draft.shift}</span>}
                                    {draft.audit_form?.product?.model_number && (
                                      <span>Model: {draft.audit_form.product.model_number}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center mt-1">
                                    <AlertCircle className="h-3 w-3 text-orange-500 mr-1" />
                                    <span className="text-xs text-orange-700 font-medium">
                                      {t('dashboard.draft')}
                                    </span>
                                  </div>
                                </div>
                                <Button
                                  onClick={() => {
                                    console.log('Resume clicked - Draft data:', {
                                      id: draft.audit_form_response_id,
                                      hasCumulativeData: !!draft.raw_response_json?.cumulativeAuditData,
                                      cumulativeData: draft.raw_response_json?.cumulativeAuditData
                                    });
                                    onResumeDraft(draft);
                                  }}
                                >
                                  <PlayCircle className="h-3 w-3 mr-1" />
                                  {t('dashboard.resume')}
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Pagination - Fixed at bottom */}
                      {getTotalPages(draftAudits) > 1 && (
                        <div className="flex-shrink-0 flex items-center justify-between mt-4 pt-4 border-t">
                          <p className="text-sm text-gray-500">
                            {t('dashboard.showing_drafts')} {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, draftAudits.length)} {t('dashboard.of_drafts')} {draftAudits.length} {t('dashboard.drafts')}
                          </p>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                              className="px-3 py-1 text-sm bg-gray-200 text-gray-600 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {t('dashboard.previous')}
                            </button>
                            {[...Array(getTotalPages(draftAudits))].map((_, i) => (
                              <button
                                key={i + 1}
                                onClick={() => setCurrentPage(i + 1)}
                                className={cn(
                                  "px-3 py-1 text-sm rounded",
                                  currentPage === i + 1
                                    ? "bg-orange-600 text-white"
                                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                                )}
                              >
                                {i + 1}
                              </button>
                            ))}
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(getTotalPages(draftAudits), prev + 1))}
                              disabled={currentPage === getTotalPages(draftAudits)}
                              className="px-3 py-1 text-sm bg-gray-200 text-gray-600 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {t('dashboard.next')}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">{t('dashboard.no_draft_audits')}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}